import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { OrganizationUpdate, useUpdateOrganisation } from "@/hooks/api/organisation/useUpdateOrganisation";
import { useGetOnboardingSuggestion } from "@/hooks/api/useGetOnboardingSuggestion";
import Button from "@/components/atoms/Button";
import Chip from "@/components/atoms/Chip";
import FormInput from "@/components/molecules/form/FormInput";
import StepHeader from "@/components/organisms/onboarding/OnboardingStepHeader";

type Props = {
  handleNext: (formData: OrganizationUpdate) => void;
  step: number;
  maxSteps: number;
  defaultValues?: OrganizationUpdate;
};

const urlRegex = /^(https?:\/\/)?([\w-])+\.([a-zA-Z0-9]{2,63})([/\w\-.~:?#[\]@!$&'()*+,;=]*)?\/?$/;

const OnboardingCompany = ({ handleNext, step, maxSteps, defaultValues }: Props) => {
  const { t } = useTranslation();
  const { updateOrganisation } = useUpdateOrganisation();
  const { data: orgaSuggestion } = useGetOnboardingSuggestion();

  const methods = useForm<OrganizationUpdate>({
    defaultValues,
  });

  const onSubmit: SubmitHandler<OrganizationUpdate> = (data) => {
    updateOrganisation(data);
    handleNext(data);
  };

  return (
    <>
      <StepHeader
        heading={t("onboarding.steps.orga.title")}
        description={t("onboarding.steps.orga.description")}
        step={step}
        maxSteps={maxSteps}
      />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className={"flex h-full flex-col"}>
          <div className={"flex flex-1 flex-col gap-8 text-left"}>
            <div>
              <FormInput
                name={"companyName"}
                label={t("onboarding.fields.company")}
                placeholder={t("onboarding.fields.company")}
                rules={{ required: t("validation.requiredName", { name: t("onboarding.fields.company") }) }}
                sizing={"xs"}
              />
              {orgaSuggestion?.company_name && (
                <div className={"pt-2"}>
                  <div className={"mb-1.5 text-xs font-medium text-tertiary"}>{t("onboarding.suggestion.company")}</div>
                  <Chip
                    label={orgaSuggestion?.company_name}
                    size={"xs"}
                    color={"natural"}
                    onClick={() => methods.setValue("companyName", orgaSuggestion.company_name!)}
                  />
                </div>
              )}
            </div>

            <div>
              <FormInput
                name={"companyUrl"}
                label={`${t("onboarding.fields.companyUrl")} (${t("validation.optional")})`}
                placeholder={"https://company-website.com"}
                rules={{
                  pattern: {
                    value: urlRegex,
                    message: t("validation.url"),
                  },
                }}
                sizing={"xs"}
              />
              {orgaSuggestion?.company_website && (
                <div className={"pt-2"}>
                  <div className={"mb-1.5 text-xs font-medium text-tertiary"}>{t("onboarding.suggestion.website")}</div>
                  <Chip
                    label={orgaSuggestion.company_website}
                    size={"xs"}
                    color={"natural"}
                    onClick={() => methods.setValue("companyUrl", orgaSuggestion.company_website!)}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={"grid grid-cols-4 gap-4"}>
            <Button type="submit" className={"col-span-2 col-start-2"} label={t("onboarding.control.next")} />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default OnboardingCompany;
